import { ArrowRight, ScanText, FileText, Zap, CheckCircle2 } from "lucide-react";

const DEMO_FIELDS = [
  { key: "invoice_number", value: "INV-2025-0042", conf: 1.0 },
  { key: "vendor_name", value: "Tyo Solution", conf: 1.0 },
  { key: "total_amount", value: "Rp 8.214.000", conf: 0.98 },
  { key: "due_date", value: "2025-09-30", conf: 1.0 },
  { key: "tax_amount", value: "Rp 902.000", conf: 0.97 },
];

export default function LiveDemo() {
  return (
    <section id="demo" className="py-24 md:py-32 px-6 md:px-10 section-light" data-testid="live-demo-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-14">
          <span className="overline-tag block mb-3">Live Demo</span>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-4">
            See the OCR API in action.
          </h2>
          <p className="text-[#94A3B8] text-lg max-w-xl mx-auto leading-relaxed">
            Upload any invoice or document and get structured JSON output instantly.
            Start with 10 bonus credits — no card required.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          {/* Left: mock terminal */}
          <div className="terminal-window" data-testid="demo-terminal">
            <div className="terminal-header flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-[#FF5F57]" />
              <div className="w-3 h-3 rounded-full bg-[#FFBD2E]" />
              <div className="w-3 h-3 rounded-full bg-[#27C93F]" />
              <span className="font-mono text-xs text-[#64748B] ml-3">invoice_2025.pdf → JSON</span>
              <span className="ml-auto text-xs text-[#22C55E] font-mono flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-[#22C55E] inline-block" />
                Extracted
              </span>
            </div>
            <div className="p-5">
              {/* Document skeleton */}
              <div className="space-y-1.5 mb-5 p-3 border border-white/[0.06] rounded-xl bg-white/[0.02]">
                <div className="h-2 rounded bg-[#38BDF8]/20 w-3/4" />
                <div className="h-2 rounded bg-white/10 w-full" />
                <div className="h-2 rounded bg-white/8 w-5/6" />
                <div className="h-2 rounded bg-[#38BDF8]/15 w-2/3" />
                <div className="h-2 rounded bg-white/10 w-full" />
              </div>

              {/* Extracted fields */}
              <div className="flex items-center gap-1.5 mb-3">
                <Zap size={12} className="text-[#38BDF8]" />
                <span className="text-[10px] font-mono uppercase tracking-widest text-[#38BDF8]">Fields Extracted</span>
              </div>
              <div className="space-y-2">
                {DEMO_FIELDS.map(field => (
                  <div key={field.key} className="flex items-center justify-between font-mono text-xs py-2 px-3 rounded-lg bg-white/[0.03] border border-white/[0.05]">
                    <span className="text-[#64748B]">{field.key}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-white font-medium">{field.value}</span>
                      <span className="text-[#22C55E] text-[10px]">{(field.conf * 100).toFixed(0)}%</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex gap-3 text-xs font-mono">
                <div className="flex-1 bg-white/[0.03] border border-white/[0.06] rounded-lg px-3 py-2">
                  <span className="text-[#64748B]">Model: </span>
                  <span className="text-[#38BDF8]">AI-Powered OCR</span>
                </div>
                <div className="flex-1 bg-white/[0.03] border border-white/[0.06] rounded-lg px-3 py-2">
                  <span className="text-[#64748B]">Credits: </span>
                  <span className="text-white">1 per page</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right: CTA */}
          <div className="space-y-7">
            <div className="space-y-4">
              {[
                { icon: ScanText, title: "Any document format", desc: "PDF, JPG, PNG — up to 10 pages per call." },
                { icon: FileText, title: "Structured JSON output", desc: "Extract any field with confidence scores." },
                { icon: CheckCircle2, title: "10 bonus credits on sign-up", desc: "No credit card required. Credits never expire." },
              ].map(item => {
                const Icon = item.icon;
                return (
                  <div key={item.title} className="flex items-start gap-4 glass-card p-5" data-testid={`demo-feature-${item.title.toLowerCase().replace(/\s+/g, "-")}`}>
                    <div className="w-10 h-10 rounded-xl bg-[#38BDF8]/10 flex items-center justify-center shrink-0">
                      <Icon size={18} className="text-[#38BDF8]" />
                    </div>
                    <div>
                      <h4 className="text-white font-semibold text-sm mb-1">{item.title}</h4>
                      <p className="text-[#94A3B8] text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <a
              href="https://ocr.tyosolution.com"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full bg-[#38BDF8] text-[#080f23] font-semibold py-4 px-7 rounded-xl hover:bg-[#0EA5E9] transition-all shadow-[0_0_30px_rgba(56,189,248,0.3)] flex items-center justify-center gap-2 group"
              data-testid="demo-cta-btn"
            >
              Try OCR Free — 10 Bonus Credits
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
